import { useResourceAssessment } from "@app/resources/hooks";
import { Assessment } from "@core/assessments/types";
import { Resource } from "@core/shared/types";
import { URLS } from "@core/shared/utils";
import { BaseLink } from "@design-system/components/link";
import { trans } from "@mongez/localization";
import { Clock } from "lucide-react";

type ResourceAssessmentProps = {
  resource: Resource;
  isLinkedResource?: boolean;
};

export function ResourceAssessment({
  resource,
  isLinkedResource = false,
}: ResourceAssessmentProps) {
  const resourceAssessment = useResourceAssessment(resource);

  // Early return if no assessment data
  if (!resourceAssessment) return null;

  const {
    status,
    buttonConfig,
    IconComponent,
    assessment,
    result,
    isInReview,
    hasFinalScore,
    resultLabel,
    resultClassName,
  } = resourceAssessment;

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-6 shadow-sm transition-all duration-300 hover:shadow-md dark:border-gray-700 dark:bg-gray-800">
      {/* Assessment Info */}
      {!isLinkedResource && (
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-700 dark:bg-blue-950/50 dark:text-blue-300">
              <Clock className="h-4 w-4" />
              <span>
                {trans("assessments.minutes", {
                  count: assessment?.duration,
                })}
              </span>
            </div>
            {assessment?.totalQuestions && (
              <div className="flex items-center gap-2 rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-700 dark:bg-green-950/50 dark:text-green-300">
                <span>
                  {trans("assessments.questions", {
                    count: assessment.totalQuestions,
                  })}
                </span>
              </div>
            )}
          </div>

          {/* Status Badge */}
          <div
            className={`rounded-full px-3 py-1 text-xs font-medium ${
              status === "passed"
                ? "bg-green-100 text-green-700 dark:bg-green-950/50 dark:text-green-300"
                : status === "failed"
                  ? "bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300"
                  : status === "active"
                    ? "bg-orange-100 text-orange-700 dark:bg-orange-950/50 dark:text-orange-300"
                    : status === "inReview" || status === "submitted"
                      ? "bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300"
                      : status === "expired"
                        ? "bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300"
                        : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200"
            }`}
          >
            {status === "notStarted" && trans("assessments.available")}
            {status === "active" && trans("assessments.active")}
            {status === "passed" && trans("assessments.passed")}
            {status === "failed" && trans("assessments.failed")}
            {(status === "inReview" || status === "submitted") &&
              trans("assessments.inReview")}
            {status === "expired" && trans("assessments.timedOut")}
          </div>
        </div>
      )}

      {/* Main Button */}
      <div className="flex justify-center">
        <BaseLink
          to={URLS.assessments.view(assessment as Assessment)}
          className={`inline-flex items-center justify-center gap-3 rounded-lg px-8 py-4 text-lg font-semibold ${buttonConfig.className}`}
          onClick={buttonConfig.onClick}
        >
          <IconComponent className="h-5 w-5" />
          <span>{buttonConfig.text}</span>
        </BaseLink>
      </div>

      {/* Review is pending; provisional totals are not a final result. */}
      {isInReview && (
        <div className="mt-4 rounded-lg bg-blue-50 p-4 text-sm text-blue-700 dark:bg-blue-950/50 dark:text-blue-300">
          {trans("assessments.reviewInProgressMessage")}
        </div>
      )}

      {result && (
        <div className="mt-4 rounded-lg bg-gray-50 p-4 dark:bg-gray-900/60">
          <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-300">
            {hasFinalScore && (
              <span>
                {trans("assessments.score")}: {result.scorePercentage}%
              </span>
            )}
            <span className={`font-medium ${resultClassName}`}>
              {resultLabel}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
